import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Stack,
    Switch,
    Paper,
    Divider,
    Collapse,
    TextField,
    Autocomplete,
    Checkbox,
    IconButton,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Chip,
    Tooltip,
    FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import AlertErrorDialog from './AlertErrorDialog';
import { ReactComponent as EditIcon } from '../../assets/PepWise2.0/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/PepWise2.0/delete-img-new.svg';
import { buildElasticsearchSourceFields } from 'utils/alertPayloadTransformers';
import {
    fetchJobListSLAConfig,
    createAlertConfig,
    updateAlertConfigs,
    getAlertConfigsByProgram,
    deleteAlertConfigById
} from 'service/service';

const ALERT_TYPE = 'JOB SLA';
const SLA_OPTIONS = ['SLA Met', 'SLA Missed', 'SLA Met, SLA Missed'];

// Common Switch Styles matching Program Tab
const commonSwitchStyles = {
    width: 44,
    height: 22,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: '4px',
        '&.Mui-checked': {
            transform: 'translateX(22px)',
            '& + .MuiSwitch-track': { backgroundColor: '#009BD8', opacity: 1, border: 'none' },
            '& .MuiSwitch-thumb': { backgroundColor: '#EFF6FF' },
        },
        '&.Mui-disabled': {
            '& + .MuiSwitch-track': { backgroundColor: '#71758F', opacity: 0.6, border: '1px solid #71758F' },
            '& .MuiSwitch-thumb': { backgroundColor: '#EFF6FF' },
        }
    },
    '& .MuiSwitch-thumb': { width: 14, height: 14, backgroundColor: '#EFF6FF', boxShadow: 'none' },
    '& .MuiSwitch-track': { borderRadius: 14, backgroundColor: '#71758F', border: '1px solid #71758F', boxSizing: 'border-box', opacity: 1 },
};

// Custom Blue Theme Input Styles matching Program Tab
const blueThemeInputStyles = {
    width: '100%',
    backgroundColor: '#F4F8FC',
    borderRadius: '4px',
    '& .MuiInputLabel-root': {
         color: '#5A6A85 !important',
         '&.Mui-focused': { color: '#003771 !important' },
         '&.MuiInputLabel-shrink': { color: '#003771 !important' },
    },
    '& .MuiInputBase-input': { 
        color: '#003771 !important',
        '-webkit-text-fill-color': '#003771 !important'
    },
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#F4F8FC',
        '& fieldset': { borderColor: '#D2E6F9 !important' },
        '&:hover fieldset': { borderColor: '#009BD8 !important' },
        '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
        '& input': { color: '#003771 !important' }
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#D2E6F9 !important',
    },
    '& .MuiSvgIcon-root': { color: '#009BD8 !important' },
    '& .MuiSelect-icon': { color: '#009BD8 !important' },
    '& .MuiSelect-select': { color: '#003771 !important' },
    '& .MuiChip-root': {
        backgroundColor: '#D0E8F9 !important',
        color: '#003771 !important',
        fontWeight: 500,
        borderRadius: '16px !important',
        margin: '2px !important',
        '& .MuiChip-label': {
            color: '#003771 !important',
        },
        '& .MuiChip-deleteIcon': {
            color: '#003771 !important',
            '&:hover': {
                color: '#001E3E !important',
            },
        },
    }
};

const lightPaperComponent = ({ children }: any) => (
    <Paper
        sx={{
            backgroundColor: '#FFFFFF !important',
            color: '#003771 !important',
            border: '1px solid #D2E6F9',
            boxShadow: '0px 4px 16px rgba(0, 55, 113, 0.08)',
            '& .MuiAutocomplete-option': {
                color: '#003771 !important',
                backgroundColor: '#FFFFFF !important',
                '&:hover': { backgroundColor: '#F4F8FC !important' },
                '&[aria-selected="true"]': { backgroundColor: '#E1EBF5 !important' }
            }
        }}
    >
        {children}
    </Paper>
);

const icon = (
    <Box
        sx={{
            width: 20,
            height: 20,
            border: '2px solid #003771',
            borderRadius: '2px',
            backgroundColor: 'transparent'
        }}
    />
);

const checkedIcon = (
    <Box
        sx={{
            width: 20,
            height: 20,
            border: '2px solid #009BD8',
            borderRadius: '2px',
            backgroundColor: '#009BD8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: '6px',
                top: '2px',
                width: '5px',
                height: '10px',
                border: 'solid white',
                borderWidth: '0 2px 2px 0',
                transform: 'rotate(45deg)'
            }
        }}
    />
);

interface JobLevelSlaEntry {
    jobId: string;
    jobName: string;
    projectName: string;
    deadline?: { type?: string };
}

interface JobAlertFormData {
    projectId: string;
    projectName: string;
    jobNames: string[];
    slaValue: string;
    channel: string;
    emailSubject: string;
    emailBody: string;
    recipients: string;
    serviceNowUrl: string;
    payload: {
        description: string;
        ConfigurationItem: string;
        resource: string;
        metric_name: string;
        AssignmentGroup: string;
        severity: string;
    };
    title: string;
    teamsMail: string;
    body: string;
}

const initialFormData: JobAlertFormData = {
    projectId: '',
    projectName: '',
    jobNames: [],
    slaValue: '',
    channel: '',
    emailSubject: '',
    emailBody: '',
    recipients: '',
    serviceNowUrl: '',
    payload: {
        description: '',
        ConfigurationItem: '',
        resource: '',
        metric_name: '',
        AssignmentGroup: '',
        severity: '2'
    },
    title: '',
    teamsMail: '',
    body: ''
};

export const JobLevelAlertsTab = ({
    programName,
    programID,
    projectData
}: {
    programName: string | null;
    programID: string | null;
    projectData: any;
}) => {
    const [jobSlaByProject, setJobSlaByProject] = useState<Record<string, JobLevelSlaEntry[]>>({});
    const [isJobListLoading, setIsJobListLoading] = useState(false);
    const [jobLevelAlerts, setJobLevelAlerts] = useState(false);

    const [tableData, setTableData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [showAddForm, setShowAddForm] = useState(false);
    const [editingAlertId, setEditingAlertId] = useState<string | null>(null);
    const [formValues, setFormValues] = useState<JobAlertFormData>(initialFormData);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; alert: any }>({ open: false, alert: null });

    const showError = (message: string) => setErrorDialog({ open: true, message });

    // Jobs that already have a Job Level SLA configured (SLATab.tsx) are the only
    // jobs that can be alerted on, so reuse the same listing call as that tab.
    useEffect(() => {
        const loadJobList = async () => {
            if (!programID) {
                setJobSlaByProject({});
                return;
            }
            setIsJobListLoading(true);
            try {
                const response: any = await fetchJobListSLAConfig({ programId: programID });
                setJobSlaByProject(response?.data?.slaList || {});
            } catch (error) {
                console.error('Failed to fetch job level SLA list:', error);
                setJobSlaByProject({});
            } finally {
                setIsJobListLoading(false);
            }
        };
        loadJobList();
    }, [programID]);

    const fetchJobLevelAlerts = async () => {
        if (!programName) {
            setTableData([]);
            return;
        }
        setIsLoading(true);
        try {
            const response: any = await getAlertConfigsByProgram(programName);
            // The list endpoint sometimes wraps the array one level deeper as
            // { totalCount, data: [...] } - mirrors AlertsTab's own fetchAlerts, which already
            // defends against both shapes.
            let alerts: any[] = [];
            if (Array.isArray(response?.data?.data)) {
                alerts = response.data.data;
            } else if (Array.isArray(response?.data?.data?.data)) {
                alerts = response.data.data.data;
            }

            if (response.success) {
                const jobAlerts = alerts
                    .filter((config: any) => config.program === programName && config.sourceConfig?.scope === 'job')
                    .map((config: any) => {
                        let channel = 'Unknown';
                        let allRecipientsString = '';
                        let recipientsForTable: string[] = [];
                        let serviceNowUrl = '';

                        if (config.emailConfig?.isEnabled) {
                            channel = 'Email';
                            recipientsForTable = config.emailConfig.emailRecipients || [];
                            allRecipientsString = recipientsForTable.join(', ');
                        } else if (config.serviceNowConfig?.isEnabled) {
                            channel = 'Service Now';
                            serviceNowUrl = config.serviceNowConfig.serviceNowUrl || '';
                            allRecipientsString = serviceNowUrl;
                        } else if (config.teamsConfig?.isEnabled) {
                            channel = 'MS Teams';
                            allRecipientsString = config.teamsConfig.teamsMail || '';
                        }

                        return {
                            id: config._id,
                            Alert_ID: config._id,
                            jobNames: config.sourceConfig?.jobNames || [],
                            jobIds: config.sourceConfig?.jobIds || [],
                            slaValue: config.sourceConfig?.slaValue || '',
                            projectName: config.project || '',
                            alertType: config.alertType || '',
                            channel,
                            allRecipients: allRecipientsString,
                            serviceNowUrl,
                            emailSubject: config.emailConfig?.emailSubject || '',
                            emailBody: config.emailConfig?.htmlTemplate || '',
                            payload: config.serviceNowConfig?.payload?.data || initialFormData.payload,
                            title: config.teamsConfig?.title || '',
                            teamsMail: config.teamsConfig?.teamsMail || '',
                            body: config.teamsConfig?.bodyTemplate || ''
                        };
                    });
                setTableData(jobAlerts);
                setJobLevelAlerts(!!jobAlerts.length)
            } else {
                setTableData([]);
            }
        } catch (error) {
            console.error('Failed to fetch job level alerts:', error);
            setTableData([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchJobLevelAlerts();
    }, [programName]);

    const projectOptions = Object.keys(jobSlaByProject).map((projectId) => ({
        projectId,
        projectName: jobSlaByProject[projectId]?.[0]?.projectName || projectId
    }));

    const jobOptionsForSelectedProject = formValues.projectId
        ? jobSlaByProject[formValues.projectId] || []
        : [];

    const handleAddRowClick = () => {
        if (showAddForm && !editingAlertId) {
            handleCancelAdd();
        } else {
            setFormValues(initialFormData);
            setEditingAlertId(null);
            setSaveError(null);
            setShowAddForm(true);
        }
    };

    const handleCancelAdd = () => {
        setShowAddForm(false);
        setEditingAlertId(null);
        setSaveError(null);
        setFormValues(initialFormData);
    };

    const handleEditAlert = (alert: any) => {
        const projectEntry = projectOptions.find((p) => p.projectName === alert.projectName);

        setFormValues({
            ...initialFormData,
            projectId: projectEntry?.projectId || '',
            projectName: alert.projectName || '',
            jobNames: alert.jobNames || [],
            slaValue: alert.slaValue || '',
            channel: alert.channel === 'Service Now' ? 'Service Now' : alert.channel,
            emailSubject: alert.emailSubject,
            emailBody: alert.emailBody,
            recipients: alert.channel === 'Email' ? alert.allRecipients : '',
            serviceNowUrl: alert.serviceNowUrl,
            payload: alert.payload,
            title: alert.title,
            teamsMail: alert.teamsMail,
            body: alert.body
        });
        setEditingAlertId(alert.Alert_ID);
        setShowAddForm(true);
    };

    const handleDeleteAlert = (alert: any) => setDeleteDialog({ open: true, alert });
    const cancelDelete = () => setDeleteDialog({ open: false, alert: null });

    const confirmDeleteAlert = async () => {
        const alert = deleteDialog.alert;
        if (!alert) return;
        try {
            setIsLoading(true);
            await deleteAlertConfigById(alert.Alert_ID);
            setDeleteDialog({ open: false, alert: null });
            await fetchJobLevelAlerts();
        } catch (error: any) {
            showError(`Failed to delete alert: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const buildChannelConfig = (config: any) => {
        if (formValues.channel === 'Email') {
            const emailRecipients = formValues.recipients.split(',').map((s) => s.trim()).filter(Boolean);
            config.emailConfig = {
                isEnabled: true,
                templateName: 'Default',
                emailSubject: formValues.emailSubject || `PEPWISE {{program_name}} - Job Level SLA Alert`,
                htmlTemplate: formValues.emailBody || `<p>The SLA Status of the job {{job_name}} with id {{job_id}} on the schedule date {{schedule_date}} is: {{sla_status}}.</p><p>Program: {{program_name}} <br> Project: {{project_name}}<br>Job Start Time: {{actual_start_time}}<br>Job End Time: {{actual_end_time}}</p>`,
                emailRecipients,
                channel: 'Email'
            };
        } else if (formValues.channel === 'Service Now') {
            config.serviceNowConfig = {
                isEnabled: true,
                templateName: 'Default',
                payload: {
                    data: {
                        description: formValues.payload?.description || `Job with id {{job_id}} and name {{job_name}} alert type: {{alert_type}}. SLA Status: {{sla_status}}`,
                        ConfigurationItem: formValues.payload?.ConfigurationItem || '',
                        resource: formValues.payload?.resource || '',
                        metric_name: formValues.payload?.metric_name || 'job_alert',
                        AssignmentGroup: formValues.payload?.AssignmentGroup || '',
                        severity: formValues.payload?.severity || '2',
                        source: 'PepWise',
                        type: 'incident'
                    }
                },
                serviceNowUrl: formValues.serviceNowUrl || '',
                channel: 'Service Now'
            };
        } else if (formValues.channel === 'MS Teams') {
            config.teamsConfig = {
                title: formValues.title || `PEPWISE {{program_name}} - Job Level SLA Alert`,
                templateName: 'Default',
                bodyTemplate: formValues.body || `Job with id {{job_id}} and name {{job_name}} alert type: {{alert_type}}. SLA Status: {{sla_status}}`,
                teamsMail: formValues.teamsMail || '',
                isEnabled: true,
                channel: 'Teams'
            };
        }
    };

    const buildJobSLAAlertConfig = () => {
        const selectedJobs = jobOptionsForSelectedProject.filter((j) => formValues.jobNames.includes(j.jobName));
        const jobIds = selectedJobs.map((j) => j.jobId);
        const jobNames = selectedJobs.map((j) => j.jobName);
        const slaStatusValues = formValues.slaValue === 'SLA Met, SLA Missed' ? ['SLA Met', 'SLA Missed'] : [formValues.slaValue];
        const projectMeta = (projectData || []).find((p: any) => p.Project_Id === formValues.projectId || p.Project_Name === formValues.projectName);
        const ruleName = `${programName}-${formValues.projectName}-JOB-SLA-${Date.now()}`.replace(/\s/g, '_');
        const esQuery = {
            _source: buildElasticsearchSourceFields([
                'labels.label.pepwise.metric.job.id',
                'labels.label.pepwise.metric.job.sla.schedule_date',
                'pepwise.body.job_name',
                'pepwise.body.sla_status',
                'pepwise.body.actual_start_time',
                'pepwise.body.actual_end_time'
            ]),
            query: {
                bool: {
                    filter: [
                        { term: { 'labels.label.pepwise.metric.kind.name': 'pepwise.metric.kind.job.sla.status' } },
                        { terms: { 'labels.label.pepwise.metric.job.id': jobIds } },
                        { terms: { 'pepwise.body.sla_status': slaStatusValues } },
                        { range: { 'pepwise.body.actual_start_time': { gte: 'now-1d' } } }
                    ]
                }
            },
            sort: [{ 'labels.label.pepwise.metric.timestamp': { order: 'desc' } }]
        };

        const config: any = {
            ruleName,
            alertStore: true,
            alertType: ALERT_TYPE,
            pepWiseAlertType: 'ALERT',
            isNewDataModel: true,
            isActive: !!programID,
            sourceConfig: {
                sourceType: 'elasticsearch',
                scope: 'job',
                indexName: 'docs-pepwise_sla_v0.3.0-pepwise_prod',
                query: JSON.stringify(esQuery),
                duplicateCheckFields: ['job_id', 'schedule_date'],
                keysToExtract: [{"key": 'labels.label.pepwise.metric.job.id', 'alias': 'job_id'}, 'job_name', 'sla_status', {'key': 'labels.label.pepwise.metric.job.sla.schedule_date', 'alias': 'schedule_date'}, 'actual_start_time', 'actual_end_time'],
                jobIds,
                jobNames,
                slaValue: formValues.slaValue
            },
            sector: projectMeta?.Sectors || '',
            project: formValues.projectName,
            program: programName
        };

        buildChannelConfig(config);
        return config;
    };

    const handleSaveAlert = async () => {
        try {
            setIsLoading(true);
            setSaveError(null);

            if (!formValues.projectId) {
                showError('Please select a project.');
                return;
            }
            if (formValues.jobNames.length === 0) {
                showError('Please select at least one job.');
                return;
            }
            if (!formValues.slaValue) {
                showError('Please select an SLA option.');
                return;
            }
            if (!formValues.channel) {
                showError('Please select a delivery channel.');
                return;
            }
            if (formValues.channel === 'Email' && !formValues.recipients.split(',').map((s) => s.trim()).filter(Boolean).length) {
                showError('Please enter at least one recipient email.');
                return;
            }
            if (formValues.channel === 'MS Teams' && !formValues.teamsMail.trim()) {
                showError('Please enter a Teams Mail address.');
                return;
            }

            const alertConfig = buildJobSLAAlertConfig();

            const isEditMode = editingAlertId !== null;
            if (isEditMode && editingAlertId) {
                const response: any = await updateAlertConfigs(editingAlertId, { ...alertConfig, isCreate: false });
                if (!response.success) {
                    throw new Error('Failed to update job level alert configuration.');
                }
            } else {
                const response: any = await createAlertConfig(alertConfig);
                if (!response.success || !response.data?.data?._id) {
                    throw new Error('Failed to create job level alert configuration.');
                }
            }

            handleCancelAdd();
            await fetchJobLevelAlerts();
        } catch (error: any) {
            const errorMsg = error?.message || 'Failed to save job level alert. Please try again.';
            setSaveError(errorMsg);
            showError(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const renderJobsChips = (jobNames: string[]) => {
        if (!jobNames || jobNames.length === 0) return null;
        return (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
                {jobNames.map((name, idx) => (
                    <Tooltip key={`${name}-${idx}`} title={name} placement="bottom">
                        <Chip
                            label={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <span>{name}</span>
                                    <span style={{ fontSize: '10px', opacity: 0.6, cursor: 'default' }}>×</span>
                                </Box>
                            }
                            size="small"
                            sx={{
                                backgroundColor: '#E1EBF5',
                                color: '#003771',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                borderRadius: '16px',
                                height: '24px',
                                maxWidth: 200
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
        );
    };

    const renderRecipientChips = (channel: string, recipients: string[], moreCount?: number, allRecipientsStr?: string) => {
        if (channel === 'Email') {
            const maxVisible = 3;
            const visibleRecipients = recipients.slice(0, maxVisible);
            const actualMore = recipients.length > maxVisible ? recipients.length - maxVisible : (moreCount || 0);
            return (
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.5}>
                    {visibleRecipients.map((rec) => {
                        const displayEmail = rec.length > 20 ? rec.substring(0, 17) + '...' : rec;
                        return (
                            <Chip
                                key={rec}
                                label={displayEmail}
                                size="small"
                                sx={{
                                    backgroundColor: '#E1EBF5',
                                    color: '#003771',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    borderRadius: '16px',
                                    height: '24px'
                                }}
                            />
                        );
                    })}
                    {actualMore > 0 ? (
                        <Typography sx={{ fontSize: '0.75rem', color: '#00AEEF', fontWeight: 'bold', ml: 0.5 }}>
                            +{actualMore} more...
                        </Typography>
                    ) : null}
                </Box>
            );
        } else if (channel === 'Service Now') {
            return (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#009BD8',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            wordBreak: 'break-all'
                        }}
                        onClick={() => allRecipientsStr && window.open(allRecipientsStr, '_blank')}
                    >
                        {allRecipientsStr}
                    </Typography>
                </Box>
            );
        } else if (channel === 'MS Teams') {
            return (
                <Typography variant="body2" sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    {allRecipientsStr}
                </Typography>
            );
        }
        return null;
    };

    const renderAddAlertForm = () => {
        let saveButtonContent: React.ReactNode;
        if (isLoading) {
            saveButtonContent = <CircularProgress size={20} sx={{ color: 'white' }} />;
        } else if (editingAlertId) {
            saveButtonContent = 'Update';
        } else {
            saveButtonContent = 'Save';
        }

        return (
            <Paper
                sx={{
                    mt: 2,
                    mb: 3,
                    p: '24px !important',
                    backgroundColor: 'transparent !important',
                    color: '#003771 !important',
                    border: '1px solid #D2E6F9 !important',
                    boxShadow: 'none',
                    borderRadius: '8px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ color: '#009BD8', fontWeight: 600, whiteSpace: 'nowrap', fontSize:'18px' }}>
                        {editingAlertId ? 'Edit Job Level Alert' : 'Add Jobs & Alert Types'}
                    </Typography>
                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px' }} />
                </Box>

                {saveError && (
                    <Typography color="error" sx={{ mt: 1, mb: 1, whiteSpace: 'pre-wrap' }}>
                        Error: {saveError}
                    </Typography>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3, mt: 2.5, mb: 3.5 }}>
                    <Autocomplete
                        sx={{ gridColumn: '1 / span 1' }}
                        options={projectOptions}
                        getOptionLabel={(option: any) => option.projectName || ''}
                        value={projectOptions.find((p) => p.projectId === formValues.projectId) || null}
                        loading={isJobListLoading}
                        popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
                        onChange={(_event: any, newValue: any) => {
                            setFormValues((prev) => ({
                                ...prev,
                                projectId: newValue?.projectId || '',
                                projectName: newValue?.projectName || '',
                                jobNames: []
                            }));
                        }}
                        renderOption={(props, option: any) => {
                            const { key, ...optionProps } = props;
                            return (
                                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                                    {option.projectName}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" placeholder="Select Project*" sx={blueThemeInputStyles} />
                        )}
                        PaperComponent={lightPaperComponent}
                    />
                    <Autocomplete
                        sx={{ gridColumn: '2 / span 1' }}
                        options={SLA_OPTIONS}
                        value={formValues.slaValue || null}
                        popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
                        onChange={(_event, newValue) => setFormValues((prev) => ({ ...prev, slaValue: newValue || '' }))}
                        getOptionLabel={(option) => (option ? `${ALERT_TYPE} - ${option}` : '')}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            return (
                                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                                    {`${ALERT_TYPE} - ${option}`}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" placeholder="Select Alert*" sx={blueThemeInputStyles} />
                        )}
                        PaperComponent={lightPaperComponent}
                    />
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        sx={{ gridColumn: '1 / span 2' }}
                        options={jobOptionsForSelectedProject.map((j) => j.jobName).filter(Boolean)}
                        value={formValues.jobNames}
                        disabled={!formValues.projectId}
                        popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
                        onChange={(_event, newValue) => setFormValues((prev) => ({ ...prev, jobNames: newValue }))}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps } = props;
                            return (
                                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8, color: '#003771' }}
                                        checked={selected}
                                        sx={{
                                            color: '#003771 !important',
                                            fill: '#003771 !important',
                                            '&.Mui-checked': { color: '#009BD8 !important', fill: '#009BD8 !important' }
                                        }}
                                    />
                                    {option}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" placeholder="Select Job(s)*" sx={blueThemeInputStyles} />
                        )}
                        PaperComponent={lightPaperComponent}
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3.5, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: '#009BD8', fontWeight: 600, whiteSpace: 'nowrap', fontSize: '18px' }}>
                        Configure Delivery Channels
                    </Typography>
                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px' }} />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2.5 }}>
                    {/* EMAIL PANEL */}
                    <Box sx={{ borderRadius: '4px', overflow: 'hidden' }}>
                        <Box
                            onClick={() => setFormValues((prev) => ({ ...prev, channel: 'Email' }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.2,
                                px: 1.5,
                                cursor: 'pointer',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                                <Switch
                                    checked={formValues.channel === 'Email'}
                                    onChange={(e) => {
                                        if (e.target.checked) setFormValues((prev) => ({ ...prev, channel: 'Email' }));
                                    }}
                                    size="small"
                                    sx={commonSwitchStyles}
                                />
                                <Typography sx={{ color: '#009BD8 !important', fontWeight: '600 !important', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                                    Email Channel
                                </Typography>
                                {formValues.channel === 'Email' && (
                                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px', ml: 1, mr: 1 }} />
                                )}
                            </Box>
                            <IconButton size="small" sx={{ color: '#009BD8' }}>
                                {formValues.channel === 'Email' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
                            </IconButton>
                        </Box>
                        <Collapse in={formValues.channel === 'Email'} timeout="auto" unmountOnExit>
                            <Box sx={{ pt: 2, pb: 1, px: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Email Subject*"
                                    variant="outlined"
                                    sx={blueThemeInputStyles}
                                    value={formValues.emailSubject}
                                    onChange={(e) => setFormValues((prev) => ({ ...prev, emailSubject: e.target.value }))}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Job {{job_name}} alert: {{alert_type}}"
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    sx={blueThemeInputStyles}
                                    value={formValues.emailBody}
                                    onChange={(e) => setFormValues((prev) => ({ ...prev, emailBody: e.target.value }))}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Select Recipient Emails or DL"
                                    variant="outlined"
                                    sx={blueThemeInputStyles}
                                    value={formValues.recipients}
                                    onChange={(e) => setFormValues((prev) => ({ ...prev, recipients: e.target.value }))}
                                    required
                                />
                            </Box>
                        </Collapse>
                    </Box>

                    {/* SERVICE NOW PANEL */}
                    <Box sx={{ borderRadius: '4px', overflow: 'hidden' }}>
                        <Box
                            onClick={() => setFormValues((prev) => ({ ...prev, channel: 'Service Now' }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.2,
                                px: 1.5,
                                cursor: 'pointer',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                                <Switch
                                    checked={formValues.channel === 'Service Now'}
                                    onChange={(e) => {
                                        if (e.target.checked) setFormValues((prev) => ({ ...prev, channel: 'Service Now' }));
                                    }}
                                    size="small"
                                    sx={commonSwitchStyles}
                                />
                                <Typography sx={{ color: '#009BD8', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                                    Service Now Channel
                                </Typography>
                                {formValues.channel === 'Service Now' && (
                                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', ml: 1, mr: 1 }} />
                                )}
                            </Box>
                            <IconButton size="small" sx={{ color: '#009BD8' }}>
                                {formValues.channel === 'Service Now' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
                            </IconButton>
                        </Box>
                        <Collapse in={formValues.channel === 'Service Now'} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <TextField fullWidth label="Description" variant="outlined" multiline rows={4} sx={blueThemeInputStyles} value={formValues.payload?.description || ''} onChange={(e) => setFormValues((prev) => ({ ...prev, payload: { ...prev.payload, description: e.target.value } }))} />
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <TextField fullWidth label="Configuration Item" variant="outlined" sx={blueThemeInputStyles} value={formValues.payload?.ConfigurationItem || ''} onChange={(e) => setFormValues((prev) => ({ ...prev, payload: { ...prev.payload, ConfigurationItem: e.target.value } }))} />
                                    <TextField fullWidth label="Resource" variant="outlined" sx={blueThemeInputStyles} value={formValues.payload?.resource || ''} onChange={(e) => setFormValues((prev) => ({ ...prev, payload: { ...prev.payload, resource: e.target.value } }))} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <TextField fullWidth label="Metric Name" variant="outlined" sx={blueThemeInputStyles} value={formValues.payload?.metric_name || ''} onChange={(e) => setFormValues((prev) => ({ ...prev, payload: { ...prev.payload, metric_name: e.target.value } }))} />
                                    <TextField fullWidth label="Assignment Group" variant="outlined" sx={blueThemeInputStyles} value={formValues.payload?.AssignmentGroup || ''} onChange={(e) => setFormValues((prev) => ({ ...prev, payload: { ...prev.payload, AssignmentGroup: e.target.value } }))} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <TextField fullWidth label="Severity" variant="outlined" sx={blueThemeInputStyles} value={formValues.payload?.severity || ''} onChange={(e) => setFormValues((prev) => ({ ...prev, payload: { ...prev.payload, severity: e.target.value } }))} placeholder="2" />
                                    <TextField fullWidth label="Service Now URL" variant="outlined" sx={blueThemeInputStyles} value={formValues.serviceNowUrl || ''} onChange={(e) => setFormValues((prev) => ({ ...prev, serviceNowUrl: e.target.value }))} />
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>

                    {/* MS TEAMS PANEL */}
                    <Box sx={{ borderRadius: '4px', overflow: 'hidden' }}>
                        <Box
                            onClick={() => setFormValues((prev) => ({ ...prev, channel: 'MS Teams' }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.2,
                                px: 1.5,
                                cursor: 'pointer',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                                <Switch
                                    checked={formValues.channel === 'MS Teams'}
                                    onChange={(e) => {
                                        if (e.target.checked) setFormValues((prev) => ({ ...prev, channel: 'MS Teams' }));
                                    }}
                                    size="small"
                                    sx={commonSwitchStyles}
                                />
                                <Typography sx={{ color: '#009BD8', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                                    MS Teams Channel
                                </Typography>
                                {formValues.channel === 'MS Teams' && (
                                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', ml: 1, mr: 1 }} />
                                )}
                            </Box>
                            <IconButton size="small" sx={{ color: '#009BD8' }}>
                                {formValues.channel === 'MS Teams' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
                            </IconButton>
                        </Box>
                        <Collapse in={formValues.channel === 'MS Teams'} timeout="auto" unmountOnExit>
                            <Box sx={{ pt: 2, pb: 1, px: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField fullWidth placeholder="Title*" variant="outlined" sx={blueThemeInputStyles} value={formValues.title} onChange={(e) => setFormValues((prev) => ({ ...prev, title: e.target.value }))} />
                                <TextField fullWidth placeholder="Job {{job_name}} alert: {{alert_type}}" variant="outlined" multiline rows={4} sx={blueThemeInputStyles} value={formValues.body} onChange={(e) => setFormValues((prev) => ({ ...prev, body: e.target.value }))} />
                                <TextField fullWidth placeholder="Teams Mail*" variant="outlined" sx={blueThemeInputStyles} value={formValues.teamsMail || ''} onChange={(e) => setFormValues((prev) => ({ ...prev, teamsMail: e.target.value }))} required />
                            </Box>
                        </Collapse>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: 2 }}>
                    <Button
                        variant="pepwiseButtonText"
                        onClick={handleCancelAdd}
                        sx={{
                            color: '#00A3E0',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            '&:hover': { backgroundColor: 'transparent', color: '#0088BC' }
                        }}
                    >
                        CANCEL
                    </Button>
                    <Button
                        variant="pepwiseSecondary"
                        onClick={handleSaveAlert}
                        sx={{
                            backgroundColor: '#00A3E0',
                            color: 'white !important',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            width: '108px !important',
                            minWidth: '108px !important',
                            maxWidth: '108px !important',
                            height: '36px',
                            p: 0,
                            borderRadius: '5px',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#0088BC', boxShadow: 'none' },
                        }}
                        disabled={isLoading}
                    >
                        {saveButtonContent}
                    </Button>
                </Box>
            </Paper>
        );
    };

    return (
        <>
        
            <FormControlLabel sx={{ml: '0px'}}
                control={
                    <Switch
                        checked={jobLevelAlerts}
                        onChange={(e) => setJobLevelAlerts(e.target.checked)}
                        size="small"
                        sx={commonSwitchStyles}
                    />
                }
                label={
                    <Typography sx={{ color: '#000000 !important', fontWeight: 500, marginLeft: '10px', fontSize: '14px' }}>
                        Configure Job Level Alerts
                    </Typography>
                }
            />
            {jobLevelAlerts && 
            <>
                <AlertErrorDialog
                    open={errorDialog.open}
                    errorMessage={errorDialog.message}
                    onClose={() => setErrorDialog({ open: false, message: '' })}
                />

                <Box mt={2} sx={{ borderRadius: '8px', border: '1px solid #D2E6F9', overflow: 'hidden' }}>
                    {/* --- Table Header --- */}
                    <Box display="flex" sx={{ backgroundColor: '#003771', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color: 'white !important' }}>
                        <Typography sx={{ width: '20%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>JOBS</Typography>
                        <Typography sx={{ width: '15%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>PROJECT</Typography>
                        <Typography sx={{ width: '20%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>ALERT TYPE</Typography>
                        <Typography sx={{ width: '15%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>CHANNEL</Typography>
                        <Typography sx={{ width: '20%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>RECIPIENT/URL</Typography>
                        <Typography sx={{ width: '10%', textAlign: 'center', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, fontSize: '0.85rem' }}>ACTIONS</Typography>
                    </Box>

                    {/* --- Table Body --- */}
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, backgroundColor: '#F4F8FC' }}>
                            <CircularProgress sx={{ color: '#003771' }} />
                        </Box>
                    ) : tableData.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, backgroundColor: '#F4F8FC' }}>
                            <Typography sx={{ color: '#003771', fontWeight: 500 }}>
                                No job level alerts configured for this program.
                            </Typography>
                        </Box>
                    ) : (
                        tableData.map((row) => (
                            <Box key={row.id} display="flex" alignItems="center" sx={{ backgroundColor: '#F4F8FC', borderBottom: '1px solid #D2E6F9', '&:last-child': { borderBottom: 'none' } }}>
                                {/* JOBS Column (20%) */}
                                <Box display="flex" alignItems="center" sx={{ width: '20%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                    {renderJobsChips(row.jobNames)}
                                </Box>

                                {/* PROJECT Column (15%) */}
                                <Box display="flex" alignItems="center" sx={{ width: '15%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                    <Typography sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.projectName}
                                    </Typography>
                                </Box>

                                {/* ALERT TYPE Column (20%) */}
                                <Box display="flex" alignItems="center" sx={{ width: '20%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                    <Typography sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.slaValue ? `${row.alertType} - ${row.slaValue}` : row.alertType}
                                    </Typography>
                                </Box>

                                {/* CHANNEL Column (15%) */}
                                <Box display="flex" alignItems="center" sx={{ width: '15%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                    <Typography sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem' }}>
                                        {row.channel}
                                    </Typography>
                                </Box>

                                {/* RECIPIENT/URL Column (20%) */}
                                <Box display="flex" alignItems="center" sx={{ width: '20%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                                    {row.channel === 'Email'
                                        ? renderRecipientChips('Email', row.allRecipients ? String(row.allRecipients).split(',').map((s: string) => s.trim()).filter(Boolean) : [])
                                        : row.channel === 'Service Now'
                                        ? renderRecipientChips('Service Now', [], 0, String(row.allRecipients || ''))
                                        : renderRecipientChips('MS Teams', [], 0, String(row.allRecipients || ''))}
                                </Box>

                                {/* ACTIONS Column (10%) */}
                                <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: '10%', py: 1.5, px: 2 }}>
                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                        <IconButton size="small" sx={{ color: '#009BD8' }} onClick={() => handleEditAlert(row)} disabled={isLoading}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" sx={{ color: '#E53E3E' }} onClick={() => handleDeleteAlert(row)} disabled={isLoading}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>

                <Typography
                    variant="body2"
                    onClick={handleAddRowClick}
                    sx={{
                        cursor: 'pointer',
                        mt: 2,
                        display: 'flex',
                        alignItems: 'center',
                        color: '#009BD8 !important',
                        fontWeight: 600
                    }}
                >
                    {showAddForm && !editingAlertId ? <RemoveIcon sx={{ mr: 1 }} /> : <AddIcon sx={{ mr: 1 }} />}
                    {showAddForm && !editingAlertId ? 'Cancel' : (editingAlertId ? 'Cancel Edit' : 'Add Job Level Alert')}
                </Typography>

                <Collapse in={showAddForm} timeout="auto" unmountOnExit>
                    {renderAddAlertForm()}
                </Collapse>

                <Dialog
                    open={deleteDialog.open}
                    onClose={cancelDelete}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'rgb(0, 55, 113)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.12)'
                        }
                    }}
                >
                    <DialogTitle sx={{ color: 'white' }}>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <DialogContentText color="inherit" sx={{ color: 'white' }}>
                            Are you sure you want to delete this job level alert for project "{deleteDialog.alert?.projectName || deleteDialog.alert?.project || ''}"?
                            <br />
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={cancelDelete} sx={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDeleteAlert}
                            sx={{
                                backgroundColor: '#E53E3E',
                                color: 'white',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: '#D32F2F' }
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>}
        </>
    );
};
